// CoreUI v3 -> v5 compatibility layer.
// Re-exports everything from @coreui/react and shims components that were
// removed or renamed between v3 and v5, so legacy views keep working while
// they are migrated to native v5 components.
import React, { useMemo, useState } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CFormCheck,
  CFormSwitch,
  CFormFeedback,
  CImage,
  CCardImage,
  CModal as CModalV5,
  CHeaderToggler,
  CSidebarToggler,
  CNavItem,
  CNavTitle,
  CNavGroup,
  CNavLink,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CProgress,
  CPagination as CPaginationV5,
  CPaginationItem,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormInput as CInputBase,
  CBreadcrumb,
  CBreadcrumbItem,
} from '@coreui/react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import CompatIconByName from './coreui-icon';

export * from '@coreui/react';
export { CompatIconByName };

/* ---------- form components (renamed in v4/v5) ---------- */

export const CLabel = ({ htmlFor, ...rest }) => <CFormLabel htmlFor={htmlFor} {...rest} />;

export const CInput = React.forwardRef((props, ref) => <CFormInput ref={ref} {...props} />);

export const CSelect = React.forwardRef(({ custom, ...props }, ref) => (
  <CFormSelect ref={ref} {...props} />
));

export const CTextarea = React.forwardRef((props, ref) => <CFormTextarea ref={ref} {...props} />);

export const CInputCheckbox = React.forwardRef(({ custom, ...props }, ref) => (
  <CFormCheck ref={ref} {...props} />
));

export const CInputRadio = React.forwardRef(({ custom, ...props }, ref) => (
  <CFormCheck type="radio" ref={ref} {...props} />
));

export const CInputFile = React.forwardRef(({ custom, ...props }, ref) => (
  <CFormInput type="file" ref={ref} {...props} />
));

export const CFormGroup = ({ row, variant, className = '', children, ...rest }) => (
  <div className={`mb-3 ${row ? 'row' : ''} ${className}`} {...rest}>{children}</div>
);

// v3 wrappers around input-group addons; in v5 children go directly in CInputGroup
export const CInputGroupPrepend = ({ children }) => <>{children}</>;
export const CInputGroupAppend = ({ children }) => <>{children}</>;

export const CInvalidFeedback = (props) => <CFormFeedback invalid {...props} />;
export const CValidFeedback = (props) => <CFormFeedback valid {...props} />;

export const CSwitch = ({
  checked, defaultChecked, onChange, color, variant, shape, size,
  labelOn, labelOff, className = '', ...rest
}) => (
  <CFormSwitch
    checked={checked}
    defaultChecked={defaultChecked}
    onChange={onChange}
    size={size === 'lg' ? 'lg' : undefined}
    className={className}
    {...rest}
  />
);

/* ---------- misc renamed / removed ---------- */

export const CImg = ({ fluid, thumbnail, ...rest }) => (
  <CImage fluid={fluid} thumbnail={thumbnail} {...rest} />
);

export const CCardImg = (props) => <CCardImage {...props} />;

export const CFade = ({ children, ...rest }) => <div {...rest}>{children}</div>;

export const CJumbotron = ({ className = '', children, ...rest }) => (
  <div className={`p-5 mb-4 bg-light rounded-3 ${className}`} {...rest}>{children}</div>
);

export const CEmbed = ({ ratio = '16by9', className = '', children, ...rest }) => (
  <div className={`ratio ratio-${ratio.replace('by', 'x')} ${className}`} {...rest}>{children}</div>
);
export const CEmbedItem = ({ children, ...rest }) => <div {...rest}>{children}</div>;

export const CToggler = ({ inHeader, ...rest }) => <CHeaderToggler {...rest} />;

export const CSubheader = ({ className = '', children, ...rest }) => (
  <div className={`header-divider border-top w-100 d-flex align-items-center px-3 py-1 ${className}`} {...rest}>
    {children}
  </div>
);

export const CSidebarMinimizer = (props) => <CSidebarToggler {...props} />;

export const CSidebarNavDivider = () => <hr className="my-2 mx-3 border-light" />;
export const CSidebarNavTitle = ({ children }) => <CNavTitle>{children}</CNavTitle>;

export const CSidebarNavItem = ({ name, to, icon, badge, children, ...rest }) => (
  <CNavItem {...rest}>
    <CNavLink to={to} as={RouterNavLink}>
      {typeof icon === 'string' ? <CompatIconByName name={icon} customClassName="nav-icon" /> : icon}
      {name || children}
    </CNavLink>
  </CNavItem>
);

export const CSidebarNavDropdown = ({ name, icon, children, ...rest }) => (
  <CNavGroup
    toggler={
      <>
        {typeof icon === 'string' ? <CompatIconByName name={icon} customClassName="nav-icon" /> : icon}
        {name}
      </>
    }
    {...rest}
  >
    {children}
  </CNavGroup>
);

// v3 config-driven renderer used with _nav.js
export const CCreateElement = ({ items = [], components = {} }) => (
  <>
    {items.map((item, idx) => {
      const { _tag, _children, ...rest } = item;
      const Comp =
        components[_tag] ||
        { CSidebarNavItem, CSidebarNavDropdown, CSidebarNavTitle, CSidebarNavDivider }[_tag] ||
        CSidebarNavItem;
      return (
        <Comp key={idx} {...rest}>
          {_children && <CCreateElement items={_children} components={components} />}
        </Comp>
      );
    })}
  </>
);

export const CHeaderNavItem = ({ children, ...rest }) => <CNavItem {...rest}>{children}</CNavItem>;
export const CHeaderNavLink = ({ to, children, ...rest }) => (
  <CNavLink to={to} as={to ? RouterNavLink : undefined} {...rest}>{children}</CNavLink>
);

export const CBreadcrumbRouter = ({ className = '' }) => {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  return (
    <CBreadcrumb className={className}>
      <CBreadcrumbItem href="/">Home</CBreadcrumbItem>
      {parts.map((p, i) => (
        <CBreadcrumbItem key={i} active={i === parts.length - 1}>
          {p.charAt(0).toUpperCase() + p.slice(1)}
        </CBreadcrumbItem>
      ))}
    </CBreadcrumb>
  );
};

/* ---------- CModal: v3 `show`/`closeOnBackdrop` -> v5 `visible`/`backdrop` ---------- */

export const CModal = ({ show, visible, closeOnBackdrop, centered, ...rest }) => (
  <CModalV5
    visible={visible !== undefined ? visible : !!show}
    backdrop={closeOnBackdrop === false ? 'static' : true}
    alignment={centered ? 'center' : undefined}
    {...rest}
  />
);

/* ---------- CTabs (v3 auto-wired tabs) ---------- */

export const CTabs = ({ activeTab, children }) => <>{children}</>;

/* ---------- v3 widgets ---------- */

export const CWidgetProgressIcon = ({
  header, text, color, inverse, value = 100, children, className = '', ...rest
}) => (
  <CCard color={color} textColor={inverse ? 'white' : undefined} className={`mb-4 ${className}`} {...rest}>
    <CCardBody>
      <div className="fs-4 fw-semibold d-flex justify-content-between">
        <span>{header}</span>
        <span>{children}</span>
      </div>
      <div>{text}</div>
      <CProgress className="mt-3 mb-0" height={4} white={!!inverse} value={value} />
    </CCardBody>
  </CCard>
);

export const CWidgetIcon = ({ header, text, color, iconPadding, children, className = '', ...rest }) => (
  <CCard className={`mb-4 ${className}`} {...rest}>
    <CCardBody className="d-flex align-items-center">
      <div className={`me-3 text-white bg-${color} p-3 rounded`}>{children}</div>
      <div>
        <div className="fs-5 fw-semibold">{header}</div>
        <div className="text-medium-emphasis text-uppercase small">{text}</div>
      </div>
    </CCardBody>
  </CCard>
);

export const CWidgetProgress = ({ header, text, footer, color, value = 100, className = '', ...rest }) => (
  <CCard className={`mb-4 ${className}`} {...rest}>
    <CCardBody>
      <div className="fs-4 fw-semibold">{header}</div>
      <div>{text}</div>
      <CProgress className="my-2" height={4} color={color} value={value} />
      <small className="text-medium-emphasis">{footer}</small>
    </CCardBody>
  </CCard>
);

export const CWidgetSimple = ({ header, text, children, className = '', ...rest }) => (
  <CCard className={`mb-4 ${className}`} {...rest}>
    <CCardBody className="text-center">
      <div className="text-medium-emphasis small text-uppercase fw-semibold">{header}</div>
      <div className="fs-6 fw-semibold py-3">{text}</div>
      {children}
    </CCardBody>
  </CCard>
);

export const CWidgetBrand = CWidgetSimple;
export const CWidgetDropdown = ({ header, text, color, footerSlot, children, className = '', ...rest }) => (
  <CCard color={color} textColor="white" className={`mb-4 ${className}`} {...rest}>
    <CCardBody className="pb-0 d-flex justify-content-between align-items-start">
      <div>
        <div className="fs-4 fw-semibold">{header}</div>
        <div>{text}</div>
      </div>
      {children}
    </CCardBody>
    {footerSlot}
  </CCard>
);

/* ---------- CPagination (v3 API: activePage / pages / onActivePageChange) ---------- */

export const CPagination = ({
  activePage = 1, pages = 1, onActivePageChange = () => {}, align = 'start', className = '', ...rest
}) => {
  if (pages <= 1) return null;
  const items = [];
  const start = Math.max(1, activePage - 2);
  const end = Math.min(pages, start + 4);
  for (let p = start; p <= end; p++) items.push(p);
  return (
    <CPaginationV5 align={align} className={className} {...rest}>
      <CPaginationItem disabled={activePage <= 1} onClick={() => onActivePageChange(activePage - 1)} style={{ cursor: 'pointer' }}>
        &laquo;
      </CPaginationItem>
      {items.map((p) => (
        <CPaginationItem key={p} active={p === activePage} onClick={() => onActivePageChange(p)} style={{ cursor: 'pointer' }}>
          {p}
        </CPaginationItem>
      ))}
      <CPaginationItem disabled={activePage >= pages} onClick={() => onActivePageChange(activePage + 1)} style={{ cursor: 'pointer' }}>
        &raquo;
      </CPaginationItem>
    </CPaginationV5>
  );
};

/* ---------- CDataTable (removed in v4+; reimplemented on CTable) ---------- */

const humanize = (key) =>
  key.replace(/[-_.]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, (c) => c.toUpperCase());

export const CDataTable = ({
  items = [],
  fields,
  itemsPerPage,
  pagination,
  tableFilter,
  sorter,
  hover,
  striped,
  bordered,
  size,
  dark,
  responsive = true,
  loading,
  clickableRows,
  onRowClick,
  scopedSlots = {},
  noItemsView,
  ...rest
}) => {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ column: null, asc: true });

  const normFields = useMemo(() => {
    const f = fields || (items[0] ? Object.keys(items[0]) : []);
    return f.map((field) =>
      typeof field === 'string'
        ? { key: field, label: humanize(field) }
        : { label: field.label !== undefined ? field.label : humanize(field.key), ...field }
    );
  }, [fields, items]);

  const filtered = useMemo(() => {
    let data = [...items];
    if (tableFilter && filter) {
      const q = filter.toLowerCase();
      data = data.filter((row) =>
        normFields.some((f) => String(row[f.key] ?? '').toLowerCase().includes(q))
      );
    }
    if (sorter && sort.column) {
      data.sort((a, b) => {
        const av = a[sort.column]; const bv = b[sort.column];
        if (av === bv) return 0;
        const res = av > bv ? 1 : -1;
        return sort.asc ? res : -res;
      });
    }
    return data;
  }, [items, filter, sort, normFields, tableFilter, sorter]);

  const perPage = itemsPerPage || filtered.length || 1;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const curPage = Math.min(page, totalPages);
  const visible = itemsPerPage ? filtered.slice((curPage - 1) * perPage, curPage * perPage) : filtered;

  return (
    <div>
      {tableFilter && (
        <CFormInput
          className="mb-3"
          style={{ maxWidth: 300 }}
          placeholder="Filter..."
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(1); }}
        />
      )}
      <CTable hover={hover} striped={striped} bordered={bordered} small={size === 'sm'} responsive={responsive} {...rest}>
        <CTableHead>
          <CTableRow>
            {normFields.map((f) => (
              <CTableHeaderCell
                key={f.key}
                style={{ ...(f._style || {}), cursor: sorter && f.sorter !== false ? 'pointer' : undefined }}
                className={f._classes}
                onClick={
                  sorter && f.sorter !== false
                    ? () => setSort((s) => ({ column: f.key, asc: s.column === f.key ? !s.asc : true }))
                    : undefined
                }
              >
                {f.label}
                {sorter && sort.column === f.key ? (sort.asc ? ' \u25B2' : ' \u25BC') : ''}
              </CTableHeaderCell>
            ))}
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {loading ? (
            <CTableRow>
              <CTableDataCell colSpan={normFields.length} className="text-center py-4">Loading...</CTableDataCell>
            </CTableRow>
          ) : visible.length === 0 ? (
            <CTableRow>
              <CTableDataCell colSpan={normFields.length} className="text-center py-4">
                {noItemsView?.noItems || 'No items'}
              </CTableDataCell>
            </CTableRow>
          ) : (
            visible.map((item, rowIdx) => (
              <CTableRow
                key={rowIdx}
                style={clickableRows ? { cursor: 'pointer' } : undefined}
                onClick={onRowClick ? () => onRowClick(item, rowIdx) : undefined}
              >
                {normFields.map((f) =>
                  scopedSlots[f.key] ? (
                    <React.Fragment key={f.key}>{scopedSlots[f.key](item, rowIdx)}</React.Fragment>
                  ) : (
                    <CTableDataCell key={f.key} className={f._classes}>
                      {item[f.key] !== undefined && item[f.key] !== null ? String(item[f.key]) : ''}
                    </CTableDataCell>
                  )
                )}
              </CTableRow>
            ))
          )}
        </CTableBody>
      </CTable>
      {pagination && totalPages > 1 && (
        <CPagination activePage={curPage} pages={totalPages} onActivePageChange={setPage} />
      )}
    </div>
  );
};

/* ---------- CIcon compat (v3 name="cil-x" -> v2 icon={cilX}) ---------- */
// (CompatIconByName imported and re-exported at top)
